import React from "react";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { PROJECTS } from "../lib/content";

const Projects = () => {
  // Only AI platforms — patents are shown in their own section, PathoGenix removed.
  const items = PROJECTS.filter(
    (p) => p.category === "AI Platform" && p.name !== "PathoGenix"
  );

  return (
    <section
      id="projects"
      data-testid="section-projects"
      className="relative py-16 sm:py-20 bg-alabaster-surface overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12">
          <div className="lg:col-span-7 reveal">
            <div className="eyebrow inline-flex items-center gap-3">
              <span className="block w-10 h-px bg-terracotta" />
              Portfolio
            </div>
            <h2
              data-testid="projects-heading"
              className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.05] tracking-tight"
            >
              Ideas that became <br />
              <span className="text-terracotta">products in production.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-ink-soft text-base sm:text-lg leading-relaxed">
              Six AI-powered platforms now in the hands of <span className="text-ink font-semibold">students</span>, <span className="text-ink font-semibold">families</span>, <span className="text-ink font-semibold">universities</span> and <span className="text-ink font-semibold">institutions</span> across the Arab world — and beyond.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {items.map((p, i) => (
            <a
              key={p.name}
              href={p.url}
              target={p.url.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={`project-card-${i}`}
              className="reveal group relative block bg-white rounded-3xl overflow-hidden border border-alabaster-line hover:border-terracotta/50 hover:shadow-[0_30px_60px_-30px_rgba(28,32,30,0.25)] transition-all duration-500 hover:-translate-y-2"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {/* Image */}
              <div className={`relative aspect-[4/3] overflow-hidden ${p.name === "Youthriving" ? "bg-white" : ""}`}>
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className={`w-full h-full transition-transform duration-700 group-hover:scale-110 ${
                    p.name === "Youthriving" ? "object-contain p-2" : "object-cover"
                  }`}
                />
                {/* Gradient overlay */}
                <div
                  className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent opacity-90"
                  aria-hidden
                />

                {/* Top-row: number + tag */}
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                  <span className="font-display text-3xl font-bold text-alabaster leading-none drop-shadow-lg">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="bg-terracotta text-ink text-[10px] font-display font-bold tracking-widest uppercase rounded-full px-3 py-1.5 shadow-lg">
                    {p.tag}
                  </span>
                </div>

                {/* Bottom-row: name + year */}
                <div className="absolute left-5 right-5 bottom-4">
                  <div className="flex items-center justify-between gap-3 text-alabaster/70 text-[11px] font-display font-bold tracking-widest uppercase mb-1">
                    <span>{p.year}</span>
                    <span className="inline-flex items-center gap-1.5 group-hover:text-terracotta-soft transition-colors">
                      Live <ExternalLink size={11} />
                    </span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-alabaster leading-tight">
                    {p.name}
                  </h3>
                </div>

                {/* Hover arrow chip */}
                <div className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-terracotta text-ink flex items-center justify-center opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 shadow-2xl">
                  <ArrowUpRight size={18} strokeWidth={2.2} />
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <p className="text-sm text-ink-soft leading-relaxed">
                  {p.blurb}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
